import { useState, useRef, useCallback, useEffect, RefObject } from 'react';
import useIsTouchDevice from '@creditflowresearch/uilib/hooks/useIsTouchDevice';
import useDelayedHover from '@creditflowresearch/uilib/PanelControl/useDelayedHover';
import { panelManager } from '@creditflowresearch/uilib/PanelControl/panelManager';
import { PANEL_CLOSE_CLEANUP_MS } from '@creditflowresearch/uilib/PanelControl/timings';
import usePanelPosition from './usePanelPosition';
import type { INavItem } from './DropdownPanelButtons';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const ACTIVE_CONTENT_SELECTOR = '[data-content-state="active"]';

const isRendered = (element: HTMLElement): boolean => (
  typeof element.checkVisibility === 'function' ? element.checkVisibility() : true
);

const focusableIn = (scope: Element | null | undefined): HTMLElement[] => (
  scope ? Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isRendered) : []
);

const focusFirstIn = (scope: Element | null | undefined): void => {
  focusableIn(scope)[0]?.focus();
};

interface IUsePanelNavigationOptions {
  items: INavItem[],
  /** Delay before opening on hover (ms) */
  delayDuration: number,
  /** Skip delay window for fast switching between items (ms) */
  skipDelayDuration: number,
  /** Delay before closing on mouse leave (ms) */
  leaveDelay: number,
}

interface IPanelNavigation {
  containerRef: RefObject<HTMLDivElement | null>,
  isPanelOpen: boolean,
  activeItemId: string | null,
  isTouchDevice: boolean,
  containerProps: {
    onMouseEnter: () => void,
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void,
    onBlur: (event: React.FocusEvent<HTMLDivElement>) => void,
  },
  itemProps: {
    onMouseEnter: (event: React.MouseEvent<HTMLDivElement>) => void,
    onFocus: (event: React.FocusEvent<HTMLDivElement>) => void,
    onClick: (event: React.MouseEvent<HTMLDivElement>) => void,
  },
}

const usePanelNavigation = ({
  items,
  delayDuration,
  skipDelayDuration,
  leaveDelay,
}: IUsePanelNavigationOptions): IPanelNavigation => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  /**
   * Keyboard override of the pointer state: `true` — opened with ArrowDown,
   * `false` — dismissed with Escape / ArrowUp, `null` — panel follows hover.
   */
  const [explicitOpen, setExplicitOpen] = useState<boolean | null>(null);
  // ArrowDown opened the panel — move focus into it once it renders (it is `inert` while closed)
  const shouldFocusPanelRef = useRef(false);

  // A coarse pointer never fires hover, so tap drives the panel there instead
  const hasCoarsePointer = useIsTouchDevice();
  // Prevent hydrataion error when switch <a> to <button> for touch devices
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isTouchDevice = isMounted && hasCoarsePointer;

  const isHovered = useDelayedHover(containerRef, {
    delayDuration,
    skipDelayDuration,
    leaveDelay,
    enabled: !isTouchDevice,
  });

  const isPanelOpen = explicitOpen ?? isHovered;

  usePanelPosition(containerRef, isPanelOpen);

  // Clear activeItemId after close animation completes
  useEffect(() => {
    if (!isPanelOpen) {
      const timer = setTimeout(() => {
        setActiveItemId(null);
      }, PANEL_CLOSE_CLEANUP_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isPanelOpen]);

  // Navigating from a panel link runs `closePanelOnNavigate` — release the override with it
  useEffect(() => panelManager.subscribe(() => {
    shouldFocusPanelRef.current = false;
    setExplicitOpen(null);
  }), []);

  // Panel is open and no longer `inert` — hand focus to its first item
  useEffect(() => {
    if (!isPanelOpen || !shouldFocusPanelRef.current) return;

    shouldFocusPanelRef.current = false;
    focusFirstIn(containerRef.current?.querySelector(ACTIVE_CONTENT_SELECTOR));
  }, [isPanelOpen, activeItemId]);

  const findTrigger = (itemId: string | null | undefined): HTMLElement | null => {
    const container = containerRef.current;
    if (!container || !itemId) return null;

    const item = container.querySelector(`[data-item-id="${ itemId }"]`);
    return focusableIn(item)[0] ?? null;
  };

  const focusActiveTrigger = (): void => {
    const trigger = findTrigger(activeItemId);
    if (trigger) {
      trigger.focus();
      return;
    }

    focusFirstIn(containerRef.current);
  };

  const openPanelForItem = (itemId: string | undefined): void => {
    const isOpenOnItem = isPanelOpen && activeItemId === itemId;
    containerRef.current?.removeAttribute('data-panel-disabled');
    if (itemId) {
      setActiveItemId(itemId);
    }
    setExplicitOpen(true);

    if (isOpenOnItem) {
      focusFirstIn(containerRef.current?.querySelector(ACTIVE_CONTENT_SELECTOR));
    } else {
      shouldFocusPanelRef.current = true;
    }
  };

  const handlePanelTab = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const panelItems = focusableIn(containerRef.current?.querySelector(ACTIVE_CONTENT_SELECTOR));

    const isLeavingForward = !event.shiftKey && event.target === panelItems[panelItems.length - 1];
    const isLeavingBackward = event.shiftKey && event.target === panelItems[0];

    if (!isLeavingForward && !isLeavingBackward) return;

    const activeIndex = items.findIndex((item) => item.id === activeItemId);
    const target = isLeavingBackward
      ? findTrigger(activeItemId)
      : findTrigger(activeIndex >= 0 ? items[activeIndex + 1]?.id : null);

    setExplicitOpen(false);

    // Last item has no next trigger — let Tab carry on out of the nav
    if (!target) return;

    event.preventDefault();
    target.focus();
  };

  const handleMouseEnter = (): void => {
    containerRef.current?.removeAttribute('data-panel-disabled');
    setExplicitOpen((current) => (current === false ? null : current));

    if (!activeItemId && items.length > 0) {
      setActiveItemId(items[0].id);
    }
  };

  const handleItemEnter = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const { itemId } = event.currentTarget.dataset;
    if (itemId) {
      setActiveItemId(itemId);
    }
  }, []);

  const handleItemClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!isTouchDevice) return;

    const { itemId } = event.currentTarget.dataset;

    if (isPanelOpen && activeItemId === itemId) {
      setExplicitOpen(false);
      return;
    }

    containerRef.current?.removeAttribute('data-panel-disabled');
    if (itemId) {
      setActiveItemId(itemId);
    }
    setExplicitOpen(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      if (!isPanelOpen) return;
      setExplicitOpen(false);
      focusActiveTrigger();
      return;
    }

    if (event.key === 'Tab') {
      if (isPanelOpen) {
        handlePanelTab(event);
      }
      return;
    }

    // Remaining keys only act on the triggers, so arrow-scrolling inside the panel keeps working
    const trigger = event.target instanceof Element ? event.target.closest('[data-item-id]') : null;
    if (!(trigger instanceof HTMLElement)) return;

    const { itemId } = trigger.dataset;

    // The trigger announces `aria-expanded`, so Space toggles it. Enter stays with the link.
    if (event.key === ' ') {
      event.preventDefault();

      if (isPanelOpen && activeItemId === itemId) {
        setExplicitOpen(false);
        return;
      }

      openPanelForItem(itemId);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openPanelForItem(itemId);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setExplicitOpen(false);
      focusActiveTrigger();
    }
  };

  // Focus reaching a different top-level item closes the panel it left behind
  const handleItemFocus = (event: React.FocusEvent<HTMLDivElement>): void => {
    const { itemId } = event.currentTarget.dataset;

    if (isPanelOpen && itemId !== activeItemId) {
      setExplicitOpen(false);
    }
  };

  // Tabbing out of the nav releases the keyboard override
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    const nextFocused = event.relatedTarget instanceof Node ? event.relatedTarget : null;

    if (!containerRef.current?.contains(nextFocused)) {
      shouldFocusPanelRef.current = false;
      setExplicitOpen(null);
    }
  };

  return {
    containerRef,
    isPanelOpen,
    activeItemId,
    isTouchDevice,
    containerProps: {
      onMouseEnter: handleMouseEnter,
      onKeyDown: handleKeyDown,
      onBlur: handleBlur,
    },
    itemProps: {
      onMouseEnter: handleItemEnter,
      onFocus: handleItemFocus,
      onClick: handleItemClick,
    },
  };
};

export default usePanelNavigation;
